import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { cn } from '../lib/utils';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import {
  ArrowLeft,
  Pencil,
  Star,
  Building2,
  Phone,
  Mail,
  MapPin,
  Globe,
  Landmark,
  BarChart,
  CheckCircle,
  XCircle,
  Loader2,
} from 'lucide-react';
import Layout from '../components/Layout';
import purchaseService from '../services/purchaseService';

const SupplierDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [supplier, setSupplier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (id) {
      loadSupplier();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadSupplier = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await purchaseService.getSupplier(id);
      setSupplier(data);
    } catch (err) {
      console.error('Error loading supplier:', err);
      setError('Failed to load supplier details');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    // Navigate back to suppliers page with edit dialog
    navigate('/suppliers', { state: { editSupplier: supplier } });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('zh-CN', {
      style: 'currency',
      currency: 'CNY',
      notation: amount > 100000 ? 'compact' : 'standard'
    }).format(amount || 0);
  };

  const getSupplierTypeLabel = (type) => {
    switch (type?.toLowerCase()) {
      case 'manufacturer': return '制造商';
      case 'distributor': return '分销商';
      case 'service': return '服务商';
      default: return '其他';
    }
  };

  const renderStars = (rating) => {
    return (
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={cn(
              "h-4 w-4",
              star <= (rating || 0) ? "text-warning fill-warning" : "text-muted-foreground/30"
            )}
          />
        ))}
        <span className="text-sm ml-2">
          ({rating || 0}/5)
        </span>
      </div>
    );
  };

  if (loading) {
    return (
      <Layout title="供应商详情" activeTab="suppliers">
        <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8 bg-muted/30 min-h-full">
          <div className="flex justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout title="供应商详情" activeTab="suppliers">
        <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8 bg-muted/30 min-h-full">
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
          <Button
            variant="outline"
            onClick={() => navigate('/suppliers')}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            返回供应商列表
          </Button>
        </div>
      </Layout>
    );
  }

  if (!supplier) {
    return (
      <Layout title="供应商详情" activeTab="suppliers">
        <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8 bg-muted/30 min-h-full">
          <Alert className="border-warning/50 bg-warning/10 mb-4">
            <AlertDescription className="text-warning">供应商不存在</AlertDescription>
          </Alert>
          <Button
            variant="outline"
            onClick={() => navigate('/suppliers')}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            返回供应商列表
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="供应商详情" activeTab="suppliers">
      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8 bg-muted/30 min-h-full">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/suppliers')}
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className="text-2xl font-bold text-foreground">
              供应商详情
            </h1>
          </div>

          <Button onClick={handleEdit}>
            <Pencil className="h-4 w-4 mr-2" />
            编辑供应商
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Basic Information */}
          <div className="md:col-span-2">
            <Card className="border bg-card">
              <CardContent className="p-6">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-20 h-20 rounded-full bg-primary flex items-center justify-center">
                    <Building2 className="h-10 w-10 text-primary-foreground" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h2 className="text-xl font-bold">
                        {supplier.supplier_name}
                      </h2>
                      {supplier.is_preferred && (
                        <Star className="h-5 w-5 text-warning fill-warning" />
                      )}
                      <Badge variant={supplier.is_active ? 'default' : 'destructive'}>
                        {supplier.is_active ? '启用' : '停用'}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground mb-2">
                      供应商编号: {supplier.supplier_code}
                    </p>
                    <Badge variant="outline">
                      {getSupplierTypeLabel(supplier.supplier_type)}
                    </Badge>
                  </div>
                </div>

                <Separator className="my-6" />

                {/* Contact Information */}
                <h3 className="text-lg font-semibold mb-4">
                  联系信息
                </h3>
                <div className="space-y-3">
                  {supplier.contact_person && (
                    <div className="flex items-start gap-3">
                      <Building2 className="h-5 w-5 text-muted-foreground mt-0.5" />
                      <div>
                        <p className="text-sm font-medium">联系人</p>
                        <p className="text-sm text-muted-foreground">{supplier.contact_person}</p>
                      </div>
                    </div>
                  )}
                  {supplier.phone && (
                    <div className="flex items-start gap-3">
                      <Phone className="h-5 w-5 text-muted-foreground mt-0.5" />
                      <div>
                        <p className="text-sm font-medium">电话</p>
                        <p className="text-sm text-muted-foreground">{supplier.phone}</p>
                      </div>
                    </div>
                  )}
                  {supplier.email && (
                    <div className="flex items-start gap-3">
                      <Mail className="h-5 w-5 text-muted-foreground mt-0.5" />
                      <div>
                        <p className="text-sm font-medium">邮箱</p>
                        <p className="text-sm text-muted-foreground">{supplier.email}</p>
                      </div>
                    </div>
                  )}
                  {(supplier.address || supplier.city || supplier.province) && (
                    <div className="flex items-start gap-3">
                      <MapPin className="h-5 w-5 text-muted-foreground mt-0.5" />
                      <div>
                        <p className="text-sm font-medium">地址</p>
                        <p className="text-sm text-muted-foreground">
                          {`${supplier.address || ''} ${supplier.city || ''} ${supplier.province || ''}`.trim()}
                        </p>
                      </div>
                    </div>
                  )}
                  {supplier.website && (
                    <div className="flex items-start gap-3">
                      <Globe className="h-5 w-5 text-muted-foreground mt-0.5" />
                      <div>
                        <p className="text-sm font-medium">网站</p>
                        <p className="text-sm text-muted-foreground">{supplier.website}</p>
                      </div>
                    </div>
                  )}
                </div>

                {supplier.description && (
                  <>
                    <Separator className="my-6" />
                    <h3 className="text-lg font-semibold mb-4">
                      公司描述
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {supplier.description}
                    </p>
                  </>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Status and Metrics */}
          <div className="space-y-4">
            {/* Rating */}
            <Card className="border bg-card">
              <CardContent className="p-6 text-center">
                <BarChart className="h-10 w-10 mx-auto mb-2 text-primary" />
                <h3 className="text-lg font-semibold mb-2">
                  供应商评级
                </h3>
                <div className="flex justify-center">
                  {renderStars(supplier.rating)}
                </div>
              </CardContent>
            </Card>

            {/* Credit Limit */}
            {supplier.credit_limit && (
              <Card className="border bg-card">
                <CardContent className="p-6 text-center">
                  <Landmark className="h-10 w-10 mx-auto mb-2 text-success" />
                  <h3 className="text-lg font-semibold mb-2">
                    信用额度
                  </h3>
                  <p className="text-2xl font-bold text-success">
                    {formatCurrency(supplier.credit_limit)}
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Status */}
            <Card className="border bg-card">
              <CardContent className="p-6 text-center">
                {supplier.is_active ? (
                  <CheckCircle className="h-10 w-10 mx-auto mb-2 text-success" />
                ) : (
                  <XCircle className="h-10 w-10 mx-auto mb-2 text-destructive" />
                )}
                <h3 className="text-lg font-semibold mb-2">
                  供应商状态
                </h3>
                <Badge variant={supplier.is_active ? 'default' : 'destructive'}>
                  {supplier.is_active ? '启用中' : '已停用'}
                </Badge>
              </CardContent>
            </Card>

            {/* Preferred Status */}
            {supplier.is_preferred && (
              <Card className="border bg-card">
                <CardContent className="p-6 text-center">
                  <Star className="h-10 w-10 mx-auto mb-2 text-warning fill-warning" />
                  <h3 className="text-lg font-semibold mb-2">
                    优选供应商
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    此供应商已被标记为优选合作伙伴
                  </p>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Additional Information */}
          {(supplier.categories && supplier.categories.length > 0) && (
            <div className="md:col-span-3">
              <Card className="border bg-card">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold mb-4">
                    供应品类
                  </h3>
                  <div className="flex gap-2 flex-wrap">
                    {supplier.categories.map((category, index) => (
                      <Badge
                        key={index}
                        variant="outline"
                      >
                        {category}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Notes */}
          {supplier.notes && (
            <div className="md:col-span-3">
              <Card className="border bg-card">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold mb-4">
                    备注信息
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {supplier.notes}
                  </p>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default SupplierDetailPage;
